"use client"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { ArrowDown, Loader2 } from "lucide-react"
import { dlomApiService, TickerLookupParams } from "@/lib/api-service"
import { yearMonthSchemaAlt } from "@/lib/date-validation"
import { DATE_RANGE } from "@/components/ui/year-month-picker"

const searchSchema = z.object({
  yearMonth: yearMonthSchemaAlt,
  tickerSymbol: z.string()
    .min(1, "Ticker symbol is required")
    .max(10, "Ticker symbol must be 10 characters or less")
    .regex(/^[A-Z]+$/, "Ticker symbol must contain only uppercase letters")
    .transform((val) => val.toUpperCase()),
  blockSize: z.number()
    .min(0.01, "Block size must be greater than 0")
    .max(99.99, "Block size must be less than 100"),
})

type SearchFormData = z.infer<typeof searchSchema>

interface TickerData {
  ticker: string
  annualizedStandardDeviation: number
  halfLife: number
  sic: string
  models: {
    model: string
    blockage: number
    dlom: number
  }[]
}

interface TickerSearchProps {
  onSearch: (data: TickerData | null) => void
  onLoadingChange: (loading: boolean) => void
}


export function TickerSearch({ onSearch, onLoadingChange }: TickerSearchProps) {
  const form = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      yearMonth: "2024-12",
      tickerSymbol: "",
      blockSize: 25,
    },
  })

  // Clear error when form values change
  const clearError = () => {
    if (form.formState.errors.root) {
      form.clearErrors("root")
    }
  }

  const onSubmit = async (formData: SearchFormData) => {
    try {
      onLoadingChange(true)
      
      // Log the form data for debugging
      console.log("Form submitted with:", formData)
      
      // Validate form data before API call
      if (!formData.yearMonth || !formData.tickerSymbol || !formData.blockSize) {
        throw new Error("Please fill in all required fields")
      }
      
      // Prepare API parameters
      const params: TickerLookupParams = {
        date: formData.yearMonth,
        ticker: formData.tickerSymbol.toUpperCase(),
        blocksize: formData.blockSize,
      }
      
      // Call the real API
      const result = await dlomApiService.lookupTicker(params)
      
      // Transform the API response to match our component's expected format
      const tickerData: TickerData = {
        ticker: result.overview.ticker,
        annualizedStandardDeviation: result.overview.annualizedStandardDeviation,
        halfLife: result.overview.halfLife,
        sic: result.overview.sic,
        models: result.discounts.map(discount => ({
          model: discount.model,
          blockage: discount.blockage,
          dlom: discount.dlom,
        })),
      }
      
      onSearch(tickerData)
    } catch (error) {
      console.error("Search error:", error)
      
      // Show user-friendly error message
      let errorMessage = "An error occurred during ticker lookup"
      if (error instanceof Error) {
        if (error.message.includes("No data found")) {
          errorMessage = "No data found for this ticker and date combination. Please try a different ticker or date."
        } else if (error.message.includes("Invalid")) {
          errorMessage = error.message
        } else if (error.message.includes("Failed to lookup ticker")) {
          errorMessage = "Unable to connect to the ticker lookup service. Please try again."
        }
      }
      
      // Set form error
      form.setError("root", {
        type: "manual",
        message: errorMessage
      })
      
      onSearch(null)
    } finally {
      onLoadingChange(false)
    }
  }

  return (
    <Card className="bg-muted">
      <CardHeader>
        <CardTitle className="text-card-foreground font-bold">Ticker Lookup</CardTitle>
        <p className="text-sm text-muted-foreground mt-2">
          {`Enter ticker symbol, date, and block size to calculate discounts. Data available from ${DATE_RANGE.MIN} to ${DATE_RANGE.MAX}.`}
        </p>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormField
                control={form.control}
                name="yearMonth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold text-foreground">Year And Month *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="2024-12" 
                        {...field}
                        onChange={(e) => {
                          field.onChange(e)
                          clearError()
                        }}
                        className="bg-background text-foreground border-border font-mono"
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {`Format: YYYY-MM (${DATE_RANGE.MIN} to ${DATE_RANGE.MAX})`}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tickerSymbol"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold text-foreground">Ticker Symbol *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="AAPL" 
                        {...field}
                        onChange={(e) => {
                          field.onChange(e.target.value.toUpperCase())
                          clearError()
                        }}
                        className="bg-background text-foreground border-border font-mono"
                        maxLength={10}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Stock symbol (e.g., AAPL, MSFT, GOOGL)
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="blockSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold text-foreground">Block Size (%) *</FormLabel>
                    <FormControl>
                      <Input 
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="99.99"
                        placeholder="25.0"
                        {...field}
                        onChange={(e) => {
                          field.onChange(parseFloat(e.target.value) || 0)
                          clearError()
                        }}
                        className="bg-background text-foreground border-border font-mono"
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Percentage between 0.01 and 99.99
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {form.formState.errors.root && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {form.formState.errors.root.message}
                </p>
              </div>
            )}

            <div className="flex justify-center">
              <Button 
                type="submit" 
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 px-8"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Looking up ticker...
                  </>
                ) : (
                  <>
                    <ArrowDown className="h-4 w-4 mr-2" />
                    Lookup Ticker
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
